import {
  DropDown,
  Flex,
  FormControls,
  IconButton,
  TextField,
} from '@elementary/common-ui/components';
import {
  AutomatedTestConfigurationApi,
  Period,
} from '@elementary/common-ui/types';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { Tooltip, Typography } from '@mui/material';
import { ReactComponent as ResetIcon } from '@elementary/common-ui/assets/20x20/reset.svg';
import { Controller, UseFormReturn } from 'react-hook-form';
import { trainingPeriodOptions } from 'shared/constants';
import TrainingPeriodInfoTooltip from 'shared/components/TrainingPeriodInfoTooltip';
import { TRAINING_PERIOD_LIMITS } from 'shared/constants';
import { validateTrainingPeriodCount } from 'shared/utils/validators';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';

const isTrainingPeriodOutOfRange = (tp: Period | null | undefined): boolean => {
  if (!tp) return false;
  const limits = TRAINING_PERIOD_LIMITS[tp.period];
  if (!limits) return true;
  return tp.count < limits.min || tp.count > limits.max;
};

type Props = {
  methods: UseFormReturn<AutomatedTestConfigurationApi>;
  readOnly?: boolean;
  savedTrainingPeriod?: Period | null;
};

const TrainingPeriodSection = ({
  methods,
  readOnly,
  savedTrainingPeriod,
}: Props) => {
  const trainingPeriod = methods.watch('anomaly_config.training_period');
  const savedIsOutOfRange = isTrainingPeriodOutOfRange(savedTrainingPeriod);
  const isReadOnly = readOnly || savedIsOutOfRange;
  const currentCount = trainingPeriod?.count;
  const currentPeriodType = trainingPeriod?.period ?? 'day';
  const limits = TRAINING_PERIOD_LIMITS[currentPeriodType];
  const isInvalid =
    !isReadOnly &&
    trainingPeriod != null &&
    (currentCount == null ||
      !limits ||
      !Number.isInteger(Number(currentCount)) ||
      Number(currentCount) < limits.min ||
      Number(currentCount) > limits.max);
  const isDirty =
    trainingPeriod?.count !== savedTrainingPeriod?.count ||
    trainingPeriod?.period !== savedTrainingPeriod?.period;

  const handleReset = () => {
    methods.setValue(
      'anomaly_config.training_period',
      savedTrainingPeriod ?? undefined,
    );
  };

  return (
    <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
      <Flex sx={{ alignItems: 'center', gap: space('xs') }}>
        <FormControls.FormTitle>Training period</FormControls.FormTitle>
        <TrainingPeriodInfoTooltip />
      </Flex>
      <FormControls.Row
        style={{
          gap: themeTokens.space.sm,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Typography component="span">Base the model on the previous</Typography>
        {savedIsOutOfRange && savedTrainingPeriod ? (
          <Typography component="span">
            {pluralizeString(
              savedTrainingPeriod.count,
              savedTrainingPeriod.period,
              `${savedTrainingPeriod.period}s`,
            )}
          </Typography>
        ) : (
          <>
            <TextField
              sx={{ width: '50px', minWidth: '50px' }}
              error={isInvalid}
              disabled={isReadOnly}
              {...methods.register('anomaly_config.training_period.count', {
                valueAsNumber: true,
                validate: (value) =>
                  validateTrainingPeriodCount(
                    value,
                    methods.getValues('anomaly_config.training_period.period'),
                    true,
                  ),
              })}
            />
            <Controller
              control={methods.control}
              name="anomaly_config.training_period.period"
              render={({ field }) => (
                <DropDown
                  sortOptions={false}
                  options={trainingPeriodOptions}
                  sx={{ minWidth: '120px' }}
                  {...field}
                  disabled={isReadOnly || field.disabled}
                  onChange={(val) => {
                    field.onChange(val);
                    methods.trigger('anomaly_config.training_period.count');
                  }}
                />
              )}
            />
          </>
        )}
        {!isReadOnly && isDirty && (
          <Tooltip title="Reset to saved value">
            <IconButton size="small" onClick={handleReset}>
              <ResetIcon />
            </IconButton>
          </Tooltip>
        )}
      </FormControls.Row>
    </FormControls.FieldsWrapper>
  );
};

export default TrainingPeriodSection;
